import { Link } from "react-router-dom";
import logo from "@/assets/tecopos-logo.png";
import {
  useLandingContent,
  useLandingContentStore,
} from "@/features/admin/content/landingContent";
import { getEnv } from "@/lib/env";
import { usePortalSession } from "@/features/releases/usePortalSession";
import {
  buildFooterColumns,
  classifyHref,
  LEGAL_LINKS,
  type FooterLink,
} from "../lib/footerLinks";

/** Glifos de marca (lucide ya no trae iconos de marca). */
function WhatsAppGlyph({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.044zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
    </svg>
  );
}
function YoutubeGlyph({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 00.5 6.2 31.2 31.2 0 000 12a31.2 31.2 0 00.5 5.8 3 3 0 002.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 002.1-2.1A31.2 31.2 0 0024 12a31.2 31.2 0 00-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z" />
    </svg>
  );
}
function FacebookGlyph({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.01 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.08 24 18.09 24 12.07z" />
    </svg>
  );
}
function InstagramGlyph({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 01-1.38-.9 3.7 3.7 0 01-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.3-1.46.72-2.12 1.38C1.35 2.67.94 3.35.63 4.14.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.3.79.72 1.46 1.38 2.12.66.66 1.34 1.08 2.12 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.86 5.86 0 002.12-1.38 5.86 5.86 0 001.38-2.12c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.86 5.86 0 00-1.38-2.12A5.86 5.86 0 0019.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1018.16 12 6.16 6.16 0 0012 5.84zm0 10.16A4 4 0 1116 12a4 4 0 01-4 4zm6.41-10.4a1.44 1.44 0 11-1.44-1.44 1.44 1.44 0 011.44 1.44z" />
    </svg>
  );
}

/** Rejilla de las columnas según cuántas sobrevivan al filtrado. */
const GRID_BY_COUNT: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-2 md:grid-cols-3",
  4: "grid-cols-2 md:grid-cols-4",
};

/**
 * Un enlace del pie: ruta de la SPA con `<Link>`; anclas de la landing, mailto y
 * externos con `<a>` (las anclas NO pueden ir con `<Link>`: react-router cambia
 * el hash sin bajar a la sección — ver `FooterLinkKind`).
 */
function FooterAnchor({ link }: { link: FooterLink }) {
  const className = "hover:text-background transition-colors";
  if (link.kind === "route") {
    return (
      <Link to={link.href} className={className}>
        {link.label}
      </Link>
    );
  }
  const blank = link.kind === "external";
  return (
    <a
      href={link.href}
      {...(blank ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className={className}
    >
      {link.label}
    </a>
  );
}

export function Footer() {
  const { social, hidden, ecosystem, businessTypes, contact } = useLandingContent();
  // El contenido del CMS solo se deriva cuando es REAL: mientras carga (o si la
  // carga falla) las vistas públicas no muestran los defaults.
  const cmsReady = useLandingContentStore((s) => s.status) === "ready";
  // Solo lectura del tokenStore (la sonda de sesión la dispara el boot de la
  // app): con sesión viva el pie lleva al panel, como el Header.
  const { isLoggedIn } = usePortalSession();
  // Enlace real a la comunidad de soporte, solo si la env está configurada
  // (goal portal-comunidad-y-tickets-organizacion; sin env, sin enlace muerto).
  const comunidadUrl = getEnv("VITE_COMUNIDAD_URL");
  const cols = buildFooterColumns({
    cmsReady,
    ecosystem,
    businessTypes,
    contact,
    hidden,
    comunidadUrl,
    isLoggedIn,
  });
  // Redes del CMS: se siguen filtrando las vacías y, además, el destino se
  // valida por esquema — son contenido editable en un `href` (igual que los
  // enlaces derivados del CMS), así que solo pasa http(s).
  const socials = [
    { key: "wa", href: social?.whatsappChannel, label: "Canal de WhatsApp", icon: <WhatsAppGlyph className="size-4" /> },
    { key: "yt", href: social?.youtube, label: "YouTube", icon: <YoutubeGlyph className="size-4" /> },
    { key: "fb", href: social?.facebook, label: "Facebook", icon: <FacebookGlyph className="size-4" /> },
    { key: "ig", href: social?.instagram, label: "Instagram", icon: <InstagramGlyph className="size-4" /> },
  ].flatMap((s) => {
    const target = classifyHref(s.href);
    return target?.kind === "external" ? [{ ...s, href: target.href }] : [];
  });

  return (
    <footer className="bg-foreground text-background pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-5 lg:px-6">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-4 space-y-5">
            <span className="inline-flex items-center gap-2">
              <span className="size-9 grid place-items-center rounded-full bg-white">
                <img src={logo} alt="" width={24} height={24} className="size-6 object-contain" />
              </span>
              <span className="text-2xl font-bold tracking-tight text-white">Teco<span className="text-primary">POS</span></span>
            </span>
            <p className="text-neutral-400 text-sm max-w-xs">
              La plataforma todo-en-uno para que tu negocio venda más, controle mejor y crezca con datos.
            </p>
            {socials.length > 0 && (
              <div className="flex gap-3 pt-2">
                {socials.map((s) => (
                  <a
                    key={s.key}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    title={s.label}
                    className="size-9 grid place-items-center rounded-full border border-white/10 text-white/80 hover:bg-white/5 hover:text-white transition-colors"
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            )}
          </div>

          {cols.length > 0 && (
            <div
              className={`lg:col-span-8 grid gap-8 ${GRID_BY_COUNT[cols.length] ?? "grid-cols-2 md:grid-cols-4"}`}
            >
              {cols.map((c) => (
                <div key={c.title}>
                  <h4 className="text-xs font-bold uppercase tracking-widest mb-5">{c.title}</h4>
                  <ul className="space-y-3 text-sm text-neutral-400">
                    {c.links.map((l) => (
                      <li key={`${l.label}-${l.href}`}>
                        <FooterAnchor link={l} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-[11px] text-neutral-500 uppercase tracking-widest">
          <span>© 2026 TecoPOS · Todos los derechos reservados · v{__APP_VERSION__}</span>
          {/* "Cookies" se retiró: no existe esa página y su contenido está
              dentro de la política de privacidad (goal portal-correcciones-3). */}
          <div className="flex gap-6">
            {LEGAL_LINKS.map((l) => (
              <Link key={l.href} to={l.href} className="hover:text-background">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
